import { Typography, useTheme } from "@mui/material";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { selectShortFridgeId } from "slices/fridgeInfoSlice";
import { selectRefill } from "slices/transactionSlice";
import { logToSentry } from "utils/logger";

import Lottie from "lottie-react";
import maintenance from "assets/animations/maintenance.json";
import { ModalContentWrapper } from "components/primitives/ModalContentWrapper";
import { ModalTitle } from "components/primitives/ModalTitle";
import { useOrder } from "hooks/useOrder";
import { useAppSelector } from "hooks/utilsHooks/storeHooks";
import { useMixpanelTrackers } from "hooks/useMixpanelTrackers";
import { TransactionState } from "types";

import { BasicModal } from "./BasicModal";
import { ModalSupportFooter } from "./ModalSupportFooter";

const RemoteRefillModalContent = () => {
	const fridgeId = useAppSelector(selectShortFridgeId);
	const { t } = useTranslation();
	const theme = useTheme();
	const { trackRemoteRefillModal } = useMixpanelTrackers();

	useEffect(() => {
		logToSentry(`Remote refill modal displayed on fridge: ${fridgeId}`, "info");
		trackRemoteRefillModal("open");

		return () => {
			trackRemoteRefillModal("close");
		};
	}, [fridgeId, trackRemoteRefillModal]);

	return (
		<ModalContentWrapper>
			<ModalTitle variant={"promoHeader"}>{t("modals.remoteRefill.title")}</ModalTitle>
			<Typography variant={"promoText"} sx={{ marginRight: "auto", marginBottom: 10 }}>
				{t("modals.remoteRefill.description")}
			</Typography>
			<Lottie animationData={maintenance} loop={true} />
			<ModalSupportFooter fridgeId={fridgeId} background={theme.palette.copper.main} />
		</ModalContentWrapper>
	);
};

const FINISH_ORDER_DELAY_MS = 10_000;

export const RemoteRefillModal = () => {
	const refill = useAppSelector(selectRefill);
	const { finishOrder } = useOrder();
	const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

	useEffect(() => {
		if (refill?.state !== TransactionState.Finished) {
			return;
		}

		timerRef.current = setTimeout(() => {
			finishOrder();
		}, FINISH_ORDER_DELAY_MS);

		return () => {
			clearTimeout(timerRef.current);
		};
	}, [refill?.state, finishOrder]);

	return (
		<BasicModal
			modalContent={<RemoteRefillModalContent />}
			open={true}
			closable={false}
			fullHeight={true}
		/>
	);
};
